import { ReactElement } from "react";
import styled, { css } from "styled-components";
import Button from "../../../atoms/Button";
import { safeHref } from "../safeUrl";

interface CtaButtonData {
  label?: string;
  url?: string;
  style?: "primary" | "secondary";
  /** 'full' = edge-to-edge button (classic mobile bottom-CTA); default centered. */
  size?: "normal" | "full";
}

const Row = styled.div`
  display: flex;
  justify-content: center;
`;

const LinkWrap = styled.a<{ $full: boolean }>`
  text-decoration: none;
  ${({ $full }) => $full && css`width: 100%;`}
`;

// 'secondary' (default) = the standard CTA look from atoms/Button. 'primary' repaints
// with the derived emphasis tone (strong tone of the chrome color, see theme/tokens.ts).
const EmphasisButton = styled(Button)<{ $emphasis: boolean; $full: boolean }>`
  ${({ $emphasis }) =>
    $emphasis &&
    css`
      background-color: var(--app-color-emphasis, var(--app-color-primary, #2db6b4));
      color: var(--app-color-emphasis-contrast, #ffffff);
      &:active {
        background-color: var(--app-color-emphasis-tint, var(--app-color-primary, #2db6b4));
      }
    `}
  ${({ $full }) =>
    $full &&
    css`
      width: 100%;
    `}
`;

const CtaButtonBlock = ({ data }: { data: CtaButtonData }): ReactElement | null => {
  if (!data.label) return null;
  const href = safeHref(data.url);
  const full = data.size === "full";
  const button = (
    <EmphasisButton $emphasis={data.style === "primary"} $full={full} type="button">
      {data.label}
    </EmphasisButton>
  );
  if (!href) return <Row>{button}</Row>;
  return (
    <Row>
      <LinkWrap
        $full={full}
        href={href}
        target={href.startsWith("https://") ? "_blank" : undefined}
        rel="noopener noreferrer"
      >
        {button}
      </LinkWrap>
    </Row>
  );
};

export default CtaButtonBlock;
